/**
 * API ماژول کار (/api/v1/work) (سند بازطراحی: گردش کار، کارهای من، چک‌لیست، وابستگی، سلامت پروژه، گزارش‌ها، اتوماسیون)
 */
import { buildQueryString, getWorkApiUrl } from '../config/api';
import { apiRequest } from './api';

const url = (endpoint, params) => {
  const base = getWorkApiUrl(endpoint.replace(/^work\//, ''));
  if (!params) return base;
  // آرایه‌ها به‌صورت «a,b» ارسال می‌شوند (بک‌اند هر دو شکل را می‌پذیرد؛ status=a&status=b در PHP فقط آخری را نگه می‌دارد)
  const normalized = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value]),
  );
  const query = buildQueryString(normalized);
  return query ? `${base}?${query}` : base;
};

const send = (method, endpoint, body) =>
  apiRequest(url(endpoint), {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

const post = (endpoint, body = {}) => send('POST', endpoint, body);
const patch = (endpoint, body) => send('PATCH', endpoint, body);
const put = (endpoint, body) => send('PUT', endpoint, body);
const del = (endpoint) => send('DELETE', endpoint);
const get = (endpoint, params) => apiRequest(url(endpoint, params));

/** دسته‌های سیستمی وضعیت وظیفه */
export const TASK_STATUS_META = {
  backlog: { label: 'بک‌لاگ', color: 'default' },
  to_do: { label: 'انجام نشده', color: 'blue' },
  in_progress: { label: 'در حال انجام', color: 'gold' },
  review: { label: 'در انتظار بررسی', color: 'purple' },
  done: { label: 'انجام شده', color: 'green' },
  canceled: { label: 'لغو شده', color: 'red' },
};

/** دسته سیستمی یک ستون کانبان (اگر category خالی است، از slug حدس زده می‌شود — مثل بک‌اند) */
export const columnCategory = (column) => {
  if (!column) return null;
  if (column.category) return column.category;
  return (
    { incomplete: 'to_do', to_do: 'to_do', doing: 'in_progress', waiting_approval: 'review', completed: 'done' }[column.slug] ||
    'in_progress'
  );
};

export const TASK_STATUS_OPTIONS = Object.entries(TASK_STATUS_META).map(([value, meta]) => ({
  value,
  label: meta.label,
}));

export const PROJECT_HEALTH_META = {
  on_track: { label: 'در مسیر', color: 'green' },
  at_risk: { label: 'در خطر', color: 'orange' },
  off_track: { label: 'خارج از مسیر', color: 'red' },
};

export const MY_WORK_FILTERS = [
  { key: 'today', label: 'امروز' },
  { key: 'overdue', label: 'عقب‌افتاده' },
  { key: 'in_progress', label: 'در حال انجام' },
  { key: 'awaiting_review', label: 'منتظر بررسی من' },
  { key: 'this_week', label: 'این هفته' },
  { key: 'no_due_date', label: 'بدون سررسید' },
];

export const PRIORITY_OPTIONS = [
  { value: 'high', label: 'بالا' },
  { value: 'medium', label: 'متوسط' },
  { value: 'low', label: 'پایین' },
];

export const workV2Api = {
  tasks: {
    list: (params = {}) => get('tasks', params),
    get: (id) => get(`tasks/${id}`),
    quickCreate: (body) => post('tasks/quick-create', body),
    bulk: (body) => post('tasks/bulk', body),
    update: (id, body) => patch(`tasks/${id}`, body),
    setAssignee: (id, assigneeId) => put(`tasks/${id}/assignee`, { assignee_id: assigneeId }),
    setCollaborators: (id, userIds) => put(`tasks/${id}/collaborators`, { user_ids: userIds }),
    archive: (id) => post(`tasks/${id}/archive`),
    unarchive: (id) => post(`tasks/${id}/unarchive`),
    trash: (id) => del(`tasks/${id}`),
    restore: (id) => post(`tasks/${id}/restore`),
    activity: (id, params = {}) => get(`tasks/${id}/activity`, params),
  },
  workflow: {
    start: (id) => post(`tasks/${id}/start`),
    submitReview: (id) => post(`tasks/${id}/submit-review`),
    approve: (id, extra = {}) => post(`tasks/${id}/approve`, extra),
    reopen: (id, reason) => post(`tasks/${id}/reopen`, { reason }),
    cancel: (id) => post(`tasks/${id}/cancel`),
    move: (id, status, extra = {}) => post(`tasks/${id}/status`, { status, ...extra }),
    block: (id, reason) => post(`tasks/${id}/block`, { reason }),
    unblock: (id) => post(`tasks/${id}/unblock`),
  },
  subtasks: {
    list: (taskId) => get(`tasks/${taskId}/subtasks`),
    create: (taskId, body) => post(`tasks/${taskId}/subtasks`, body),
    update: (taskId, id, body) => patch(`tasks/${taskId}/subtasks/${id}`, body),
    remove: (taskId, id) => del(`tasks/${taskId}/subtasks/${id}`),
    reorder: (taskId, ids) => post(`tasks/${taskId}/subtasks/reorder`, { ids }),
  },
  checklist: {
    list: (taskId) => get(`tasks/${taskId}/checklist`),
    create: (taskId, title) => post(`tasks/${taskId}/checklist`, { title }),
    update: (taskId, id, body) => patch(`tasks/${taskId}/checklist/${id}`, body),
    remove: (taskId, id) => del(`tasks/${taskId}/checklist/${id}`),
    reorder: (taskId, ids) => post(`tasks/${taskId}/checklist/reorder`, { ids }),
  },
  dependencies: {
    list: (taskId) => get(`tasks/${taskId}/dependencies`),
    add: (taskId, blockingTaskId) => post(`tasks/${taskId}/dependencies`, { blocking_task_id: blockingTaskId }),
    remove: (taskId, blockingTaskId) => del(`tasks/${taskId}/dependencies/${blockingTaskId}`),
  },
  links: {
    types: () => get('link-types'),
    options: (type, q = '') => get('link-options', { type, q }),
    set: (taskId, links) => put(`tasks/${taskId}/links`, { links: links.map(({ type, id }) => ({ type, id })) }),
    tasksFor: (type, id, params = {}) => get('linked-tasks', { type, id, ...params }),
  },
  myWork: (params = {}) => get('my-work', params),
  notifications: {
    list: (params = {}) => get('work/notifications', params),
    markRead: (id) => post(`work/notifications/${id}/read`),
    markAllRead: () => post('work/notifications/read-all'),
  },
  projects: {
    get: (id) => get(`projects/${id}`),
    milestones: (id) => get(`projects/${id}/milestones`),
    updateHealth: (id, health, healthReason) => put(`projects/${id}/health`, { health, health_reason: healthReason }),
    archive: (id) => post(`projects/${id}/archive`),
    unarchive: (id) => post(`projects/${id}/unarchive`),
    activity: (id, params = {}) => get(`projects/${id}/activity`, params),
    statuses: () => get('projects/statuses'),
    updateStatusCategory: (statusId, category) => patch(`projects/statuses/${statusId}`, { category }),
    createStatus: (body) => post('projects/statuses', body),
    updateStatus: (statusId, body) => patch(`projects/statuses/${statusId}`, body),
    removeStatus: (statusId) => del(`projects/statuses/${statusId}`),
  },
  timeLogs: {
    create: (body) => post('time-logs', body),
    update: (id, body) => patch(`time-logs/${id}`, body),
    remove: (id) => del(`time-logs/${id}`),
    approve: (id) => post(`time-logs/${id}/approve`),
  },
  reports: {
    cycleTime: (params = {}) => get('work/reports/cycle-time', params),
    overdueRate: (params = {}) => get('work/reports/overdue-rate', params),
    workload: (params = {}) => get('work/reports/workload', params),
  },
  savedViews: {
    list: () => get('work/saved-views'),
    create: (body) => post('work/saved-views', body),
    update: (id, body) => patch(`work/saved-views/${id}`, body),
    remove: (id) => del(`work/saved-views/${id}`),
  },
  settings: {
    get: () => get('work/settings'),
    update: (body) => put('work/settings', body),
  },
  automation: {
    list: () => get('work/automation-rules'),
    create: (body) => post('work/automation-rules', body),
    update: (id, body) => patch(`work/automation-rules/${id}`, body),
    remove: (id) => del(`work/automation-rules/${id}`),
  },
};

/** پیام خطای قابل نمایش از خطای apiRequest */
export const workErrorMessage = (error, fallback = 'خطا در انجام عملیات') => {
  if (!error) return fallback;
  const details = error.details || error.errors;
  if (details && typeof details === 'object') {
    const first = Object.values(details).flat()[0];
    if (first) return String(first);
  }
  return error.message || fallback;
};
